import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { getMilestones } from "../../api/process";
import { useParams } from "react-router-dom";
import WidgetCard from "./WidgetCard";
import useDraggableScroll from "../../hooks/useDraggableScroll";

export default function MobileMilestoneWidget() {
  const today = new Date();
  const { id: projectId } = useParams();
  const [milestones, setMilestones] = useState([]);
  const viewMode = "month"; // ✅ 모바일은 월간 고정
  const { ref, ...dragProps } = useDraggableScroll();

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const data = await getMilestones(projectId);
        console.log("마일스톤 스케줄 데이터:", data);
        setMilestones(data);
      } catch (error) {
        console.error("마일스톤 스케줄 데이터 불러오기 실패:", error);
      }
    };
    fetchMilestones();
  }, [projectId]);

  // ✅ 안전하게 날짜 처리
  const safeDate = (d) => (d ? new Date(d).getTime() : null);
  const formatDate = (d) => (d ? d.slice(5).replace("-", "/") : "-");

  // ✅ Baseline (맨 위 레인)
  const baselineSeries = milestones
    .filter((m) => m.baseline)
    .map((m) => ({
      x: safeDate(m.baseline),
      y: 1.1,
      name: `${m.name} (${formatDate(m.baseline)})`,
    }));

  // ✅ Actual (가운데 레인, 오늘까지만)
  const actualSeries = milestones
    .filter((m) => m.actual)
    .map((m) => {
      const act = new Date(m.actual);
      if (act <= today) {
        return {
          x: act.getTime(),
          y: 1.0,
          name: `${m.name} (${formatDate(m.actual)})`,
        };
      } else {
        const lastBeforeToday = milestones.find(
          (ms) => new Date(ms.actual) < today
        );
        if (lastBeforeToday)
          return {
            x: today.getTime(),
            y: 1.0,
            name: `${m.name} (진행 중)`,
            isToday: true,
          };
        return null;
      }
    })
    .filter(Boolean);

  // ✅ Forecast (맨 아래 레인, 오늘 이후)
  const actualForecastSeries = [];
  let lastActualBeforeToday = null;

  for (const m of milestones) {
    const act = new Date(m.actual);
    if (act <= today) lastActualBeforeToday = act;
  }

  for (const m of milestones) {
    const act = new Date(m.actual);
    if (act > today) {
      if (actualForecastSeries.length === 0 && lastActualBeforeToday) {
        actualForecastSeries.push({
          x: today.getTime(),
          y: 0.9,
          name: "진행 중",
        });
      }
      actualForecastSeries.push({
        x: act.getTime(),
        y: 0.9,
        name: `${m.name} (${formatDate(m.actual)})`,
      });
    }
  }

  const series = [
    { name: "Baseline", data: baselineSeries },
    { name: "Actual", data: actualSeries },
    { name: "Forecast", data: actualForecastSeries },
  ];

  // ✅ x축 범위 계산 - 모바일은 항상 프로젝트 전체 구간을 '월간' 수준의 해상도로 길게 표시
  let minX = null;
  let maxX = null;
  if (milestones.length > 0) {
    const dates = milestones
      .map((m) => new Date(m.baseline || m.actual))
      .filter((d) => !isNaN(d));
    const start = new Date(Math.min(...dates));
    const end = new Date(Math.max(...dates));

    // 전체 구간의 앞뒤로 약간의 여백 추가
    minX = new Date(start);
    minX.setDate(minX.getDate() - 15);
    maxX = new Date(end);
    maxX.setDate(maxX.getDate() + 15);
  }

  const options = {
    chart: {
      type: "line",
      height: 200,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: { format: "yyyy-MM-dd" },
      y: {
        formatter: (val, opts) => {
          const { seriesIndex, dataPointIndex, w } = opts;
          const currentX = w.config.series[seriesIndex].data[dataPointIndex].x;

          const point = w.config.series[seriesIndex].data[dataPointIndex];

          if (point.name === "진행 중" || point.isToday) return "";

          const pointsOnSameDate = [];
          w.config.series.forEach((s, i) => {
            s.data.forEach((p) => {
              if (p.x === currentX) {
                pointsOnSameDate.push({
                  seriesName: w.config.series[i].name,
                  name: p.name?.split(" (")[0],
                  x: p.x,
                });
              }
            });
          });

          const baselineMap = {};
          const actualMap = {};
          w.config.series[0].data.forEach((b) => {
            baselineMap[b.name?.split(" (")[0]] = b.x;
          });
          [...w.config.series[1].data, ...w.config.series[2].data].forEach(
            (a) => {
              actualMap[a.name?.split(" (")[0]] = a.x;
            }
          );

          let html = "";
          pointsOnSameDate.forEach((p) => {
            const color =
              p.seriesName === "Baseline"
                ? "#3F72AF"
                : p.seriesName === "Actual"
                  ? "#FE7743"
                  : "#F4A261";

            const baselineX = baselineMap[p.name];
            const actualX = actualMap[p.name];
            let diffText = "";
            if (baselineX && actualX && p.seriesName !== "Baseline") {
              const diffDays = Math.round(
                (actualX - baselineX) / (1000 * 60 * 60 * 24)
              );
              if (diffDays > 0)
                diffText = `<span style="color:#e74c3c;"> (지연 ${diffDays}일)</span>`;
              else if (diffDays < 0)
                diffText = `<span style="color:#3498db;"> (단축 ${Math.abs(
                  diffDays
                )}일)</span>`;
              else diffText = `<span style="color:#777;"> (변동 없음)</span>`;
            }

            html += `<div style="margin:2px 0;color:${color};font-weight:500;">
          • ${p.name}${diffText}
        </div>`;
          });

          return html || "<span style='color:#aaa;'>데이터 없음</span>";
        },
        title: { formatter: () => "" },
      },
      style: { fontSize: "11px" },
      marker: { show: true },
    },

    stroke: {
      width: [2, 2, 2],
      curve: "straight",
      dashArray: [0, 0, 5],
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      discrete: [
        ...actualSeries
          .map((p, i) =>
            p.isToday ? { seriesIndex: 1, dataPointIndex: i, size: 0 } : null
          )
          .filter(Boolean),
        { seriesIndex: 2, dataPointIndex: 0, size: 0 },
      ],
    },
    colors: ["#3F72AF", "#FE7743", "#F4A261"],
    xaxis: {
      type: "datetime",
      min: minX ? minX.getTime() : undefined,
      max: maxX ? maxX.getTime() : undefined,
      labels: {
        datetimeFormatter: { month: "M월", year: "yyyy" },
        style: { fontSize: "10px" },
      },
    },
    yaxis: {
      min: 0.8,
      max: 1.2,
      tickAmount: 2,
      labels: {
        formatter: (val) => {
          if (val >= 1.05) return "Baseline";
          if (val <= 0.95) return "Forecast";
          return "Actual";
        },
        style: { fontSize: "11px", fontWeight: 500 },
      },
    },
    annotations: {
      xaxis: [
        {
          x: today.getTime(),
          strokeDashArray: 4,
          borderColor: "#b1b1b1ff",
          label: { show: false },
        },
      ],
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0, 1],
      formatter: (_, opts) => {
        const point =
          opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];

        if (point.name === "진행 중" || point.isToday) return "";
        return point.name;
      },
      background: { enabled: false },
      offsetY: -6,
      style: { fontSize: "9px", colors: ["#555"] },
    },
    grid: {
      borderColor: "#eee",
      strokeDashArray: 3,
      padding: { top: 5, bottom: 5, left: 10, right: 10 },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontSize: "11px",
      markers: { radius: 4 },
    },
  };

  return (
    <WidgetCard title="Milestone Schedule">
      <div className="flex flex-col gap-3">
        {/* 차트 영역 - 드래그 스크롤 적용 */}
        <div
          ref={ref}
          {...dragProps}
          className="w-full overflow-x-auto cursor-grab active:cursor-grabbing select-none scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="min-w-[2000px]">
            <Chart options={options} series={series} type="line" height={190} width="100%" />
          </div>
        </div>
      </div>
    </WidgetCard>
  );
}
